<svelte:options runes={false} />
<script>
  // The running-strategies list (U8): the caller's deployed strategies, selectable. Empty / loading / fetch-error
  // states are explicit (a blank region must never read as "no data" when it is actually "loading" or "failed").
  import { createEventDispatcher } from 'svelte';
  import SymbolUniverse from '../shared/SymbolUniverse.svelte';
  import { parseSymbols, summarizeSymbols } from '../../lib/symbolUniverse.js';
  import { targetOf, targetBadge, isRealMoney } from '../../lib/runContext.js';

  const dispatch = createEventDispatcher();

  // The row IS a <button>, so the pair strip renders non-expandable (a static "+N" badge — nested controls
  // are invalid HTML); the operator expands the universe in the workspace header instead. The tooltip
  // carries a bounded summary, never the raw 400-name string (TASK-20260823).
  const rowTitle = (symbols) => summarizeSymbols(parseSymbols(symbols), 6);

  export let strategies = [];     // LiveRunSnapshot[]
  export let selectedId = null;
  export let loading = false;     // getStrategies in flight
  export let error = null;        // fetch error message

  function statusClass(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'running') return 'at-status-running';
    if (s === 'paused' || s === 'pausing') return 'at-status-paused';
    if (s === 'faulted') return 'at-status-faulted';
    if (s === 'stopped' || s === 'stopping' || s === 'undeploying') return 'at-status-stopped';
    return 'at-status-deployed';
  }
</script>

<div class="at-running-list">
  <div class="at-running-head">
    <h3 class="at-running-title">Strategies</h3>
    <button type="button" class="btn btn-ghost btn-sm" on:click={() => dispatch('refresh')}
      disabled={loading} aria-label="Refresh strategies">↻</button>
  </div>

  {#if error}
    <div class="alert alert-danger at-running-error" role="alert">
      {error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={() => dispatch('refresh')}>Retry</button>
    </div>
  {:else if loading}
    <div class="at-skeleton-rows" aria-busy="true" aria-label="Loading strategies">
      {#each Array(3) as _, i (i)}<div class="at-skeleton-row"></div>{/each}
    </div>
  {:else if strategies.length === 0}
    <div class="empty-state">
      <p class="empty-state-description">No strategies deployed. Use the form above to deploy one.</p>
    </div>
  {:else}
    <ul class="at-strategy-rows" role="listbox" aria-label="Deployed strategies">
      {#each strategies as s (s.strategyId)}
        <li role="option" aria-selected={s.strategyId === selectedId}>
          <button type="button" class="at-strategy-row" class:selected={s.strategyId === selectedId}
            title={rowTitle(s.symbols)}
            on:click={() => dispatch('select', s.strategyId)}>
            <span class="at-strategy-main">
              <span class="at-strategy-name">{s.name ?? s.strategy}</span>
              <span class="at-strategy-symbols">
                <span class="at-strategy-kind">{s.strategy}</span>
                <SymbolUniverse symbols={s.symbols} dense expandable={false} label="Pairs" />
              </span>
            </span>
            <span class="at-strategy-meta">
              <!-- The target pill derives from the row's PERSISTED target (ADR-0040): LIVE only for real money,
                   UNKNOWN (never Live, never silently Paper) when the row cannot be classified. -->
              <span class="at-mode-pill" class:live={isRealMoney(targetOf(s))}>{targetBadge(targetOf(s))}</span>
              <span class="at-status-pill {statusClass(s.status)}">{s.status}</span>
            </span>
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  /* Kind + one-row pair strip. The main column takes the remaining row width (basis 0 — never content-sized,
     or a narrowed fit could never re-grow on widen); the strip owns what is left after the kind. */
  .at-strategy-main { flex: 1 1 0; }
  .at-strategy-symbols { display: flex; align-items: center; gap: 0.4rem; min-width: 0; }
  .at-strategy-kind { flex-shrink: 0; }
  .at-strategy-symbols > :global(.su-root) { flex: 1 1 0; }
</style>

